import React, { useEffect, useState } from "react";

export function EditThreadModal({
  isOpen,
  onClose,
  onSave,
  loading = false,
  initialTitle = "",
  initialContent = "",
  initialUsePseudonym = false,
  initialImageUrl = "",
}) {
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [usePseudonym, setUsePseudonym] = useState(initialUsePseudonym);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initialImageUrl || null);

  useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setContent(initialContent);
      setUsePseudonym(initialUsePseudonym);
      setImageFile(null);
      setImagePreview(initialImageUrl || null);
    }
  }, [isOpen, initialTitle, initialContent, initialUsePseudonym, initialImageUrl]);

  if (!isOpen) return null;

  const onSelectImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Ukuran file maksimal 5MB");
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("File harus berupa gambar");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const onRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      alert("Judul dan isi thread harus diisi");
      return;
    }
    onSave?.({
      title: title.trim(),
      content: content.trim(),
      use_pseudonym: !!usePseudonym,
      imageFile, // null means keep existing or no image
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg mx-4 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
          aria-label="Close"
          disabled={loading}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <h2 className="text-lg font-semibold mb-4">Edit Thread</h2>

        <form onSubmit={handleSave}>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Judul Thread
            </label>
            <input
              type="text"
              placeholder="Masukkan judul thread"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={100}
              disabled={loading}
              required
            />
            <p className="text-xs text-gray-500 mt-1">{title.length}/100 karakter</p>
          </div>

          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Isi Thread
            </label>
            <textarea
              placeholder="Tulis isi thread Anda di sini..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={6}
              disabled={loading}
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gambar (Opsional)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept="image/*"
                id="edit-thread-image"
                className="hidden"
                onChange={onSelectImage}
                disabled={loading}
              />
              <label
                htmlFor="edit-thread-image"
                className="cursor-pointer px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {imagePreview ? "Ganti Gambar" : "Upload Gambar"}
              </label>
              {imagePreview && (
                <button
                  type="button"
                  onClick={onRemoveImage}
                  className="px-3 py-2 text-sm text-red-600 hover:text-red-800 transition-colors"
                  disabled={loading}
                >
                  Hapus Gambar
                </button>
              )}
            </div>
            {imagePreview && (
              <div className="mt-3">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg border"
                />
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Maksimal 5MB, format: JPG, PNG, GIF
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-3 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={usePseudonym}
                  onChange={(e) => setUsePseudonym(e.target.checked)}
                  className="sr-only"
                  disabled={loading}
                />
                <div
                  className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                    usePseudonym
                      ? "bg-blue-500 border-blue-500"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  {usePseudonym && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-700">
                Gunakan nama samaran
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-8">
              Centang untuk menggunakan nama samaran alih-alih username asli
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim() || !content.trim()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? "Menyimpan..." : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function EditReplyModal({
    isOpen,
    onClose,
    onSave,
    loading = false,
    // initialTitle = "",
    initialContent = "",
    initialUsePseudonym = false,
    initialImageUrl = "",
  }) {
    // const [title, setTitle] = useState(initialTitle);
    const [content, setContent] = useState(initialContent);
    const [usePseudonym, setUsePseudonym] = useState(initialUsePseudonym);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(initialImageUrl || null);
  
    useEffect(() => {
      if (isOpen) {
        // setTitle(initialTitle);
        setContent(initialContent);
        setUsePseudonym(initialUsePseudonym);
        setImageFile(null);
        setImagePreview(initialImageUrl || null);
      }
    }, [isOpen, initialContent, initialUsePseudonym, initialImageUrl]);
  
    if (!isOpen) return null;
  
    const onSelectImage = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Ukuran file maksimal 5MB");
        return;
      }
  
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("File harus berupa gambar");
        return;
      }
  
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    };
  
    const onRemoveImage = () => {
      setImageFile(null);
      setImagePreview(null);
    };
  
    const handleSave = async (e) => {
      e.preventDefault();
      if (!content.trim()) {
        alert("isi thread harus diisi");
        return;
      }
      onSave?.({
        // title: title.trim(),
        content: content.trim(),
        use_pseudonym: !!usePseudonym,
        imageFile, // null means keep existing or no image
      });
    };
  
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-lg mx-4 relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
            aria-label="Close"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
  
          <h2 className="text-lg font-semibold mb-4">Edit Thread</h2>
  
          <form onSubmit={handleSave}>
            {/* <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Judul Thread
              </label>
              <input
                type="text"
                placeholder="Masukkan judul thread"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={100}
                disabled={loading}
                required
              />
              <p className="text-xs text-gray-500 mt-1">{title.length}/100 karakter</p>
            </div> */}
  
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Isi Thread
              </label>
              <textarea
                placeholder="Tulis isi thread Anda di sini..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={6}
                disabled={loading}
                required
              />
            </div>
  
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gambar (Opsional)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*"
                  id="edit-thread-image"
                  className="hidden"
                  onChange={onSelectImage}
                  disabled={loading}
                />
                <label
                  htmlFor="edit-thread-image"
                  className="cursor-pointer px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {imagePreview ? "Ganti Gambar" : "Upload Gambar"}
                </label>
                {imagePreview && (
                  <button
                    type="button"
                    onClick={onRemoveImage}
                    className="px-3 py-2 text-sm text-red-600 hover:text-red-800 transition-colors"
                    disabled={loading}
                  >
                    Hapus Gambar
                  </button>
                )}
              </div>
              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg border"
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Maksimal 5MB, format: JPG, PNG, GIF
              </p>
            </div>
  
            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={usePseudonym}
                    onChange={(e) => setUsePseudonym(e.target.checked)}
                    className="sr-only"
                    disabled={loading}
                  />
                  <div
                    className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-colors ${
                      usePseudonym
                        ? "bg-blue-500 border-blue-500"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    {usePseudonym && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm text-gray-700">
                  Gunakan nama samaran
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-8">
                Centang untuk menggunakan nama samaran alih-alih username asli
              </p>
            </div>
  
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={loading}
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading || !content.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? "Menyimpan..." : "Simpan Perubahan"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

export function DeleteThreadModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  threadTitle = "",
}) {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-lg relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
          aria-label="Close"
          disabled={loading}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Hapus Thread?
          </h3>
          
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-3">
              Apakah Anda yakin ingin menghapus thread ini?
            </p>
            {threadTitle && (
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-sm font-medium text-gray-800 truncate">
                  "{threadTitle}"
                </p>
              </div>
            )}
            <p className="text-xs text-red-600 mt-3">
              Tindakan ini tidak dapat dibatalkan. Semua balasan akan ikut terhapus.
            </p>
          </div>

          <div className="flex justify-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Batal
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-red-300 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Menghapus..." : "Hapus Thread"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}